import React, { useEffect, useRef } from 'react';
import '../styles/FarePickerModal.css';

/**
 * KNOWN ISSUE: Duffel sandbox data may have incomplete baggage information.
 * Example: Singapore Airlines Economy typically includes 1-2x23kg checked bags,
 * but sandbox may return checkedBagCount: null. Production API should have accurate data.
 * 
 * To inspect fare data: Open DevTools Console when this modal opens.
 * Data is auto-saved to localStorage.lastFareData for debugging.
 */

// Baggage allowance constants
const CABIN_ALLOWANCE_BY_CLASS = {
  economy: { checkIn: '25 kg', cabin: '7 kg' },
  'premium economy': { checkIn: '30 kg', cabin: '10 kg' },
  business: { checkIn: '40 kg', cabin: '14 kg' },
  'first class': { checkIn: '50 kg', cabin: '15 kg' },
};

const AIRLINE_ALLOWANCE_OVERRIDES = {
  '6E': { checkIn: '15 kg', cabin: '7 kg' },
  AI: { checkIn: '25 kg', cabin: '7 kg' },
  EK: { checkIn: '30 kg', cabin: '7 kg' },
  EY: { checkIn: '30 kg', cabin: '7 kg' },
  QR: { checkIn: '30 kg', cabin: '7 kg' },
  UL: { checkIn: '30 kg', cabin: '7 kg' },
  SQ: { checkIn: '30 kg', cabin: '7 kg' },
  QF: { checkIn: '30 kg', cabin: '7 kg' },
  VN: { checkIn: '23 kg', cabin: '12 kg' },
};

// Renders a real per-fare weight/count into display text; returns null when the provider
// gave no data at all for this field, so the caller can fall back to a static estimate.
function formatBagAllowance(weightKg, count) {
  if (weightKg === 0 || count === 0) return 'Not included';
  if (weightKg > 0) return `${weightKg} kg`;
  if (count > 0) return `${count} ${count === 1 ? 'piece' : 'pieces'}`;
  return null;
}

// Prefers the actual per-fare baggage allowance returned by the provider (Duffel/Mystifly);
// only falls back to the static per-airline/cabin-class table when the provider supplied no
// baggage data at all (e.g. Local flights, or a provider offer missing fareConditions).
function getAllowanceForSegment(carrierCode, cabinClass, fareConditions) {
  const normalizedCarrier = String(carrierCode || '').toUpperCase();
  const normalizedCabin = String(cabinClass || 'Economy').toLowerCase();
  const byClass = CABIN_ALLOWANCE_BY_CLASS[normalizedCabin] || CABIN_ALLOWANCE_BY_CLASS.economy;
  const override = AIRLINE_ALLOWANCE_OVERRIDES[normalizedCarrier];

  return {
    checkIn: formatBagAllowance(fareConditions?.checkedBagWeightKg, fareConditions?.checkedBagCount)
      ?? override?.checkIn ?? byClass.checkIn,
    cabin: formatBagAllowance(fareConditions?.cabinBagWeightKg, fareConditions?.cabinBagCount)
      ?? override?.cabin ?? byClass.cabin,
  };
}

/**
 * Derives a human-readable fare tier label when the provider did not supply one.
 * Priority: provider fareName → conditions-based derivation → positional fallback.
 */
function deriveFareName(fc, index) {
  if (fc?.fareName) return fc.fareName;
  if (!fc) return index === 0 ? 'BASIC' : index === 1 ? 'STANDARD' : 'FLEX';
  const hasChecked = fc.checkedBagCount > 0 || fc.checkedBagWeightKg > 0;
  const isFullyRefundable = fc.refundAllowed && !fc.refundPenaltyAmount;
  if (isFullyRefundable) return 'FLEX';
  if (hasChecked) return 'STANDARD';
  return 'LIGHT';
}

function FareCard({ flight, index, cheapestRawPrice, totalCount, onSelect, formatPrice, allFlights, isSelectingReturn }) {
  const fc = flight.fareConditions || null;
  const fareName = deriveFareName(fc, index);
  
  // Calculate baggage allowance based on cabin class
  const segments = flight.segments || [];
  const carrierCode = segments[0]?.carrierCode || flight.airline;
  const cabinClass = flight.class || 'Economy';
  const baggageInfo = getAllowanceForSegment(carrierCode, cabinClass, fc);
  
  // Check if fare options actually differ in meaningful ways
  const hasFareData = fc && (fc.checkedBagCount != null || fc.changesAllowed != null || fc.refundAllowed != null);
  
  // Check if this flight has better benefits than the cheapest option
  const cheapestFc = allFlights[0]?.fareConditions;
  const hasBetterBaggage = (fc?.checkedBagCount || 0) > (cheapestFc?.checkedBagCount || 0);
  const hasBetterRefund = fc?.refundAllowed && (!cheapestFc?.refundAllowed || 
                          (fc.refundPenaltyAmount || Infinity) < (cheapestFc?.refundPenaltyAmount || Infinity));
  const hasBetterChanges = fc?.changesAllowed && (!cheapestFc?.changesAllowed || 
                           (fc.changePenaltyAmount || Infinity) < (cheapestFc?.changePenaltyAmount || Infinity));
  
  const hasActualBenefits = hasBetterBaggage || hasBetterRefund || hasBetterChanges;
  
  // Only show badges when this option has ACTUAL superior benefits
  const isBestValue = hasActualBenefits && totalCount >= 2 && index === totalCount - 1;
  const isRecommended = hasActualBenefits && totalCount >= 3 && index === 1 && !isBestValue;

  // Simplified baggage info
  // For Economy and above: if backend returns null, assume baggage is included
  const isBasicFare = fareName === 'LIGHT' || fareName === 'BASIC';
  const hasBaggage = fc?.checkedBagCount > 0 || fc?.checkedBagWeightKg > 0 || 
                     (!isBasicFare && fc?.checkedBagCount == null && fc?.checkedBagWeightKg == null);
  
  // Simplified flexibility info
  const isFullyRefundable = fc?.refundAllowed && !fc?.refundPenaltyAmount;
  const hasFlexibleChanges = fc?.changesAllowed && !fc?.changePenaltyAmount;
  const hasAnyFlex = fc?.changesAllowed || fc?.refundAllowed;

  const priceParts = formatPrice(flight);

  return (
    <div 
      className={`fpm-card${isRecommended ? ' fpm-card--recommended' : ''}${isBestValue ? ' fpm-card--premium' : ''}`}
      onClick={() => onSelect(flight)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(flight);
        }
      }}
    >
      {isRecommended && (
        <div className="fpm-badge fpm-badge--recommended">Most Popular</div>
      )}
      {isBestValue && (
        <div className="fpm-badge fpm-badge--premium">Best Value</div>
      )}
      
      <div className="fpm-card__header">
        <span className="fpm-card__fare-name">{fareName}</span>
        {flight.segments?.[0]?.bookingClass && (
          <span className="fpm-card__booking-class">
            {flight.segments[0].bookingClass}
          </span>
        )}
      </div>
      
      <div className="fpm-card__price">
        <span className="fpm-card__price-currency">{priceParts.currency}</span>
        <span className="fpm-card__price-amount">{priceParts.amount}</span>
        <span className="fpm-card__price-pax">
          {isSelectingReturn && flight.combinedOfferPrice != null ? 'Round Trip Total' : 'per adult'}
        </span>
      </div>

      {/* Simplified benefits */}
      <div className="fpm-benefits">
        {/* Always show actual baggage allowances */}
        <div className="fpm-benefit">
          <span className="fpm-benefit__icon" aria-hidden="true" style={{ fontSize: '13px' }}>🧳</span>
          <span>Check-in: {baggageInfo.checkIn}</span>
        </div>
        <div className="fpm-benefit">
          <span className="fpm-benefit__icon" aria-hidden="true" style={{ fontSize: '13px' }}>🎒</span>
          <span>Cabin: {baggageInfo.cabin}</span>
        </div>

        {/* Refund/Change policies - Always show */}
        {hasAnyFlex ? (
          <>
            {hasFlexibleChanges && (
              <div className="fpm-benefit fpm-benefit--highlight">
                <span className="fpm-benefit__icon" aria-hidden="true" style={{ fontSize: '13px' }}>✅</span>
                <span>Free date changes</span>
              </div>
            )}
            {isFullyRefundable && (
              <div className="fpm-benefit fpm-benefit--highlight">
                <span className="fpm-benefit__icon" aria-hidden="true" style={{ fontSize: '13px' }}>✅</span>
                <span>Fully refundable</span>
              </div>
            )}
            {!hasFlexibleChanges && fc?.changesAllowed && (
              <div className="fpm-benefit">
                <span className="fpm-benefit__icon" aria-hidden="true" style={{ fontSize: '13px' }}>⚠️</span>
                <span>Cancellation with fees</span>
              </div>
            )}
            {!isFullyRefundable && fc?.refundAllowed && (
              <div className="fpm-benefit">
                <span className="fpm-benefit__icon" aria-hidden="true" style={{ fontSize: '13px' }}>⚠️</span>
                <span>Refundable with fees</span>
              </div>
            )}
          </>
        ) : (
          <>
            {/* When no fare data, show default policies */}
            {!hasFareData ? (
              <>
                <div className="fpm-benefit">
                  <span className="fpm-benefit__icon" aria-hidden="true" style={{ fontSize: '13px' }}>⚠️</span>
                  <span>Refundable with fees</span>
                </div>
                <div className="fpm-benefit">
                  <span className="fpm-benefit__icon" aria-hidden="true" style={{ fontSize: '13px' }}>⚠️</span>
                  <span>Cancellation with fees</span>
                </div>
              </>
            ) : (
              <div className="fpm-benefit fpm-benefit--none">
                <span className="fpm-benefit__icon" aria-hidden="true" style={{ fontSize: '13px' }}>❌</span>
                <span>Non-refundable</span>
              </div>
            )}
          </>
        )}
        
        {hasFareData && !hasActualBenefits && index > 0 && (
          <div className="fpm-benefit fpm-benefit--warning">
            <span className="fpm-benefit__icon" aria-hidden="true" style={{ fontSize: '13px' }}>⚠️</span>
            <span>Same visible benefits as lower-priced option. Price difference may reflect booking class, seat selection, or availability.</span>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * FarePickerModal
 * - `flights`      : array of Flight objects sharing the same physical flight, sorted cheapest first
 * - `isRoundTrip`  : whether the user is on a round-trip journey
 * - `onSelect`     : (flight) => void — called when user picks a fare
 * - `onClose`      : () => void
 * - `formatPrice`  : (flight) => { currency, amount } — from parent to reuse currency/fx logic
 * - `routeLabel`   : "MEL → DEL" string for display
 * - `airlineName`  : display name
 * - `dateLabel`    : "Thu, 2 Apr 26"
 */
function FarePickerModal({
  flights,
  isRoundTrip,
  isSelectingReturn,
  onSelect,
  onClose,
  formatPrice,
  routeLabel,
  airlineName,
  dateLabel,
}) {
  const overlayRef = useRef(null);
  const prevFocusRef = useRef(null);
  const dialogRef = useRef(null);

  // Deduplicate flights based on price and fare conditions
  const uniqueFlights = React.useMemo(() => {
    const seen = new Map();
    const deduplicated = [];
    
    for (const flight of flights) {
      const fc = flight.fareConditions;
      // Create a unique key based on price and all fare condition properties
      const key = JSON.stringify({
        price: flight.price,
        fareName: fc?.fareName,
        cabinBagCount: fc?.cabinBagCount,
        cabinBagWeightKg: fc?.cabinBagWeightKg,
        checkedBagCount: fc?.checkedBagCount,
        checkedBagWeightKg: fc?.checkedBagWeightKg,
        changesAllowed: fc?.changesAllowed,
        changePenaltyAmount: fc?.changePenaltyAmount,
        refundAllowed: fc?.refundAllowed,
        refundPenaltyAmount: fc?.refundPenaltyAmount,
      });
      
      if (!seen.has(key)) {
        seen.set(key, true);
        deduplicated.push(flight);
      }
    }
    
    return deduplicated;
  }, [flights]);

  const count = uniqueFlights.length;

  // On open: save focused element, focus first focusable child; restore on close
  useEffect(() => {
    prevFocusRef.current = document.activeElement;
    const focusable = dialogRef.current?.querySelectorAll(
      'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable?.length) focusable[0].focus();
    return () => { prevFocusRef.current?.focus(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Tab trapping
  useEffect(() => {
    const onTab = (e) => {
      if (e.key !== 'Tab') return;
      const focusable = [
        ...(dialogRef.current?.querySelectorAll(
          'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        ) || []),
      ];
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onTab);
    return () => document.removeEventListener('keydown', onTab);
  }, []);

  // Close on Escape (separate from tab-trap listener, to keep concerns clear)
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Close on backdrop click
  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  return (
    <div
      className="fpm-overlay"
      ref={overlayRef}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label="Fare options"
    >
      <div className="fpm-dialog" ref={dialogRef}>
        {/* Header */}
        <div className="fpm-header">
          <p className="fpm-header__count">
            <strong>{count} FARE OPTION{count !== 1 ? 'S' : ''}</strong> available. Choose your option!
          </p>
          <button
            type="button"
            className="fpm-close"
            onClick={onClose}
            aria-label="Close fare picker"
          >
            <span style={{ fontSize: '16px' }}>✖</span>
          </button>
        </div>

        {/* Route summary */}
        <div className="fpm-route">
          <span className="fpm-route__label">{routeLabel}</span>
          <span className="fpm-route__dot" aria-hidden="true">·</span>
          <span className="fpm-route__airline">{airlineName}</span>
          <span className="fpm-route__dot" aria-hidden="true">·</span>
          <span className="fpm-route__date">{dateLabel}</span>
        </div>

        {/* Fare cards */}
        <div
          className="fpm-cards"
          style={{ '--fpm-col-count': Math.min(count, 4) }}
        >
          {uniqueFlights.map((flight, index) => (
            <FareCard
              key={flight.code}
              flight={flight}
              index={index}
              totalCount={count}
              cheapestRawPrice={uniqueFlights[0]?.price ?? flight.price}
              onSelect={onSelect}
              formatPrice={formatPrice}
              allFlights={uniqueFlights}
              isSelectingReturn={isSelectingReturn}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default FarePickerModal;
